import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { EngineProvider, ProductEngine } from '@sindi/product-engine/react';
import '@sindi/product-engine/styles.css';
import UserContext from '../libs/context';
import { post } from '../libs/api';
import { useProductEngineScope } from '../components/form/productEngineHost';
import { createClientEngineServices } from './clientEngineServices';
import CopySourceSearch from './CopySourceSearch';
import './productEngine.css';

const CAPABILITIES = {
  evaluation: true,
  wallet: true,
  submission: true,
  upload: true,
  dates: true,
  annuity: true,
  indexes: true,
  securities: true,
  selectOptions: true,
};
const PRESENTATION = {
  eyebrow: 'Structured Product',
  confirmationTitle: 'Trade booked successfully',
  confirmationStatus: 'Pending Approval',
  bookAnotherLabel: 'Book Another',
  viewRecordLabel: 'View My Investments',
};

// Client route/identity/navigation policy wraps the package-owned trade form.
export default function GenericTradeEntry({ schema, sourceCurrency }) {
  const navigate = useNavigate();
  const user = useContext(UserContext)?.currentUser;
  const scope = useProductEngineScope({
    structureVersionId: schema?.structureVersionId,
  });
  const timer = useRef(null);
  const clearRedirect = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
  }, []);

  // Only an agent who manages a client sees the copy panel. The server makes the same check.
  const [copyEligible, setCopyEligible] = useState(false);
  const [copySources, setCopySources] = useState(null);
  const [copySourcesError, setCopySourcesError] = useState('');
  const [copySourcesLoading, setCopySourcesLoading] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [copyFields, setCopyFields] = useState(null);
  const [copyUnmatchedFields, setCopyUnmatchedFields] = useState([]);
  const [copyFieldsError, setCopyFieldsError] = useState('');
  const [resetSequence, setResetSequence] = useState(0);

  useEffect(() => {
    let active = true;
    post('/user/get')
      .then((response) => {
        if (!active) return;
        const sessionUser = response?.data || {};
        setCopyEligible(sessionUser.role === 'agent' && !!sessionUser.manage_user);
      })
      .catch(() => {
        if (active) setCopyEligible(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const services = useMemo(
    () =>
      createClientEngineServices({
        sourceTransactionId: copySource ? copySource.transactionId : null,
      }),
    [copySource]
  );

  const resetCopyState = useCallback(() => {
    setCopySources(null);
    setCopySourcesError('');
    setCopySourcesLoading(false);
    setCopySource(null);
    setCopyFields(null);
    setCopyUnmatchedFields([]);
    setCopyFieldsError('');
  }, []);

  const loadCopySources = useCallback(() => {
    if (copySources || copySourcesLoading || !schema?.structureVersionId) return;
    setCopySourcesLoading(true);
    setCopySourcesError('');
    services
      .listCopySources(schema.structureVersionId)
      .then((rows) => setCopySources(Array.isArray(rows) ? rows : []))
      .catch((error) =>
        setCopySourcesError(error?.message || 'Unable to load existing trades to copy.')
      )
      .finally(() => setCopySourcesLoading(false));
  }, [services, copySources, copySourcesLoading, schema?.structureVersionId]);

  const copyFieldLabel = useCallback(
    (key) => schema?.fields?.[key]?.label || schema?.fields?.[key]?.title || key,
    [schema?.fields]
  );

  const chooseCopySource = useCallback(
    (row) => {
      if (!schema?.structureVersionId) return;
      setCopyFieldsError('');
      services
        .loadCopySourceFields(schema.structureVersionId, row.transactionId)
        .then((payload) => {
          setCopySource(row);
          setCopyFields(payload?.fields || {});
          setCopyUnmatchedFields(
            Array.isArray(payload?.unmatchedFields) ? payload.unmatchedFields : []
          );
          // ProductEngine only re-seeds its form state on remount (its key below), so this
          // forces one — otherwise the copied values would be ignored after the initial mount.
          setResetSequence((value) => value + 1);
        })
        .catch((error) =>
          setCopyFieldsError(error?.message || 'Unable to load the source trade.')
        );
    },
    [services, schema?.structureVersionId]
  );

  const clearCopySource = useCallback(() => {
    resetCopyState();
    setResetSequence((value) => value + 1);
  }, [resetCopyState]);

  useEffect(
    () => clearRedirect,
    [
      clearRedirect,
      schema?.structureVersionId,
      sourceCurrency,
      scope.hostId,
      scope.tenantId,
      scope.schemaContext,
      user?.id,
    ]
  );
  const onSubmitted = useCallback(
    (result) => {
      clearRedirect();
      const template =
        schema?.submission?.successRedirect ||
        '/portal/investment/new_order/:transactionId';
      const redirect = template.replace(
        /:(\w+)/g,
        (match, key) =>
          result[key] ??
          result[key.replace(/_([a-z])/g, (_, ch) => ch.toUpperCase())] ??
          result[key.replace(/([A-Z])/g, (_, ch) => `_${ch.toLowerCase()}`)] ??
          match
      );
      timer.current = setTimeout(() => navigate(redirect), 500);
    },
    [schema?.submission?.successRedirect, navigate, clearRedirect]
  );

  const initialValues = copyFields
    ? copyFields
    : sourceCurrency
    ? { investmentCurrency: sourceCurrency.toUpperCase() }
    : undefined;
  const initialOrigins = copyFields
    ? Object.keys(copyFields).reduce((origins, key) => {
        origins[key] = 'USER';
        return origins;
      }, {})
    : sourceCurrency
    ? { investmentCurrency: 'USER' }
    : undefined;

  return (
    <EngineProvider
      services={services}
      capabilities={CAPABILITIES}
      cacheScope={{ ...scope, userId: user?.id }}
    >
      {copyEligible ? (
        <CopySourceSearch
          source={copySource}
          unmatchedFields={copyUnmatchedFields}
          fieldLabel={copyFieldLabel}
          sources={copySources}
          sourcesError={copySourcesError}
          sourcesLoading={copySourcesLoading}
          fieldsError={copyFieldsError}
          onSearchStart={loadCopySources}
          onChoose={chooseCopySource}
          onClear={clearCopySource}
        />
      ) : null}
      <ProductEngine
        key={`${sourceCurrency || ''}:${resetSequence}`}
        schema={schema}
        initialValues={initialValues}
        initialOrigins={initialOrigins}
        presentation={{
          ...PRESENTATION,
          submitLabel: schema?.submission?.buttonText || 'Invest',
        }}
        onSubmitted={onSubmitted}
        onBookAnother={() => {
          clearRedirect();
          navigate(0);
        }}
        onViewRecord={() => {
          clearRedirect();
          navigate('/portal/investment');
        }}
      />
    </EngineProvider>
  );
}
